import React from 'react';
import { Download, X, Check, AlertCircle, Loader2, Package } from 'lucide-react';
import { useDownloadProgress, DownloadJob } from '../contexts/DownloadProgressContext';

function formatBytes(bytes: number): string {
  if (!bytes || bytes < 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function JobCard({ job, onDismiss, onCancel }: { job: DownloadJob; onDismiss: () => void; onCancel: () => void }) {
  let statusEl: React.ReactNode;
  let pct = 0;
  let progressColor = 'bg-blue-500';

  if (job.status === 'preparing') {
    statusEl = (
      <div className="flex items-center gap-1.5 text-xs text-blue-700 dark:text-blue-400">
        <Loader2 size={12} className="animate-spin" /> Starting…
      </div>
    );
    pct = 2;
  } else if (job.status === 'staging') {
    // Phase 1: server-side staging — show file count, since bytes_staged
    // can lag behind file count for small files.
    pct = job.fileCount > 0 ? Math.min(95, Math.round((job.stagedCount / job.fileCount) * 100)) : 5;
    statusEl = (
      <div className="text-xs text-blue-700 dark:text-blue-400">
        <span className="flex items-center gap-1.5">
          <Package size={12} /> Preparing on server: <span className="font-mono">{job.stagedCount}</span> / <span className="font-mono">{job.fileCount}</span> files
        </span>
        {job.bytesStaged > 0 && (
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            {formatBytes(job.bytesStaged)} staged
          </div>
        )}
      </div>
    );
  } else if (job.status === 'building') {
    pct = 98;
    statusEl = (
      <div className="flex items-center gap-1.5 text-xs text-blue-700 dark:text-blue-400">
        <Loader2 size={12} className="animate-spin" /> Building zip…
      </div>
    );
  } else if (job.status === 'downloading') {
    // Phase 3: actual byte-level download progress
    pct = job.totalBytes > 0 ? Math.min(100, Math.round((job.bytesReceived / job.totalBytes) * 100)) : 0;
    statusEl = (
      <div className="text-xs text-blue-700 dark:text-blue-400">
        <span className="font-mono">{formatBytes(job.bytesReceived)}</span>
        {job.totalBytes > 0 ? <> / <span className="font-mono">{formatBytes(job.totalBytes)}</span></> : null}
        {' '}({pct}%)
      </div>
    );
  } else if (job.status === 'done') {
    pct = 100;
    progressColor = 'bg-green-500';
    statusEl = (
      <div className="flex items-center gap-1.5 text-xs text-green-700 dark:text-green-400">
        <Check size={12} /> Saved to Downloads
      </div>
    );
  } else {
    progressColor = 'bg-red-500';
    statusEl = (
      <div className="flex items-center gap-1.5 text-xs text-red-700 dark:text-red-400">
        <AlertCircle size={12} /> {job.errorMessage || 'Failed'}
      </div>
    );
  }

  const inFlight = ['preparing', 'staging', 'building', 'downloading'].includes(job.status);

  return (
    <div className={`bg-card border rounded-lg shadow-lg p-3 w-80 transition-all ${
      job.status === 'failed' ? 'border-red-300 dark:border-red-700'
      : job.status === 'done' ? 'border-green-300 dark:border-green-700'
      : 'border-border'
    }`}>
      <div className="flex items-start gap-2">
        <Download size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{job.label}</p>
          <p className="text-[11px] text-muted-foreground">
            {job.fileCount} file{job.fileCount === 1 ? '' : 's'}
            {job.totalBytes > 0 && job.status !== 'downloading' && job.status !== 'done' ? ` · ${formatBytes(job.totalBytes)}` : ''}
          </p>
        </div>
        <button
          onClick={inFlight ? onCancel : onDismiss}
          className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
          title={inFlight ? 'Cancel' : 'Dismiss'}
        >
          <X size={14} />
        </button>
      </div>
      {(inFlight || job.status === 'done') && (
        <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${progressColor}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
      <div className="mt-1.5">{statusEl}</div>
    </div>
  );
}

/** Bottom-right toast stack of active and recent download jobs. */
export function DownloadProgressWidget() {
  const { jobs, dismiss, cancel } = useDownloadProgress();
  if (jobs.length === 0) return null;
  return (
    <div className="fixed bottom-4 right-4 z-[100] space-y-2 max-w-sm">
      {jobs.map(job => (
        <JobCard key={job.id} job={job} onDismiss={() => dismiss(job.id)} onCancel={() => cancel(job.id)} />
      ))}
    </div>
  );
}
